import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import useAdminAuth from '../hooks/useAdminAuth';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConfirmModal from '../components/ConfirmModal';
import { Plus, Trash2, Image as ImageIcon, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminGallery = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  useAdminAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploadMethod, setUploadMethod] = useState('url'); // 'url' or 'file'
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [formData, setFormData] = useState({
    image_url: '',
    caption: '',
    display_order: 0
  });

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load gallery images',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Invalid File',
          description: 'Please select an image file',
          variant: 'destructive'
        });
        return;
      }
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: 'Please select an image smaller than 5MB',
          variant: 'destructive'
        });
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, image_url: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast({
        title: 'No File Selected',
        description: 'Please select an image file',
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        
        // Create gallery image with base64 data
        const imageData = {
          image_url: base64Image,
          caption: formData.caption,
          display_order: parseInt(formData.display_order) || 0
        };
        
        await axios.post(`${API}/gallery`, imageData);
        
        toast({
          title: 'Success',
          description: 'Image uploaded successfully'
        });
        
        setShowAddForm(false);
        setSelectedFile(null);
        setFormData({
          image_url: '',
          caption: '',
          display_order: 0
        });
        
        fetchImages();
      };
      
      reader.onerror = () => {
        toast({
          title: 'Upload Error',
          description: 'Failed to read image file',
          variant: 'destructive'
        });
      };
      
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to upload image',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleAddImage = async (e) => {
    e.preventDefault();
    
    // If file upload method, use handleFileUpload
    if (uploadMethod === 'file') {
      await handleFileUpload();
      return;
    }
    
    // URL method
    if (!formData.image_url) {
      toast({
        title: 'Missing Information',
        description: 'Please provide an image URL',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/gallery`, formData);
      
      toast({
        title: 'Success',
        description: 'Image added to gallery'
      });
      
      setShowAddForm(false);
      setFormData({
        image_url: '',
        caption: '',
        display_order: 0
      });
      
      fetchImages();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to add image',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    setImageToDelete(imageId);
    setShowDeleteModal(true);
  };

  const confirmDeleteImage = async () => {
    if (!imageToDelete) return;
    
    setUploading(true);
    try {
      await axios.delete(`${API}/gallery/${imageToDelete}`);
      
      toast({
        title: 'Success',
        description: 'Image deleted successfully'
      });
      
      setShowDeleteModal(false);
      setImageToDelete(null);
      fetchImages();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete image',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  if (loading && images.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p>Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#1e3a5f]">Gallery Management</h1>
              <p className="text-gray-600 mt-2">Manage homepage gallery images</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/admin')}
                variant="outline"
                className="border-gray-300"
              >
                Back to Dashboard
              </Button>
              <Button
                onClick={() => setShowAddForm(!showAddForm)}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Image
              </Button>
            </div>
          </div>

          {/* Instructions */}
          <Card className="p-6 mb-6 bg-blue-50 border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-2">How to Upload Images</h3>
            <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
              <li>Upload your image to a free image hosting service like <a href="https://imgur.com" target="_blank" rel="noopener noreferrer" className="underline">Imgur</a> or <a href="https://postimages.org" target="_blank" rel="noopener noreferrer" className="underline">PostImages</a></li>
              <li>Copy the direct image URL (must end in .jpg, .png, .webp, etc.)</li>
              <li>Paste the URL below and add a caption (optional)</li>
              <li>Click "Add to Gallery"</li>
            </ol>
          </Card>

          {/* Add Image Form */}
          {showAddForm && (
            <Card className="p-6 mb-8">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-6">Add New Image</h2>
              
              {/* Upload Method Toggle */}
              <div className="mb-6 flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setUploadMethod('file');
                    setFormData(prev => ({ ...prev, image_url: '' }));
                    setSelectedFile(null);
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                    uploadMethod === 'file'
                      ? 'border-green-600 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Upload className="w-5 h-5 inline mr-2" />
                  Upload File
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setUploadMethod('url');
                    setSelectedFile(null);
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                    uploadMethod === 'url'
                      ? 'border-green-600 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <ImageIcon className="w-5 h-5 inline mr-2" />
                  Image URL
                </button>
              </div>
              
              <form onSubmit={handleAddImage} className="space-y-6">
                {uploadMethod === 'file' ? (
                  <div>
                    <Label htmlFor="file_upload">Select Image File *</Label>
                    <input
                      id="file_upload"
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="mt-2 block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-lg file:border-0
                        file:text-sm file:font-semibold
                        file:bg-green-50 file:text-green-700
                        hover:file:bg-green-100
                        cursor-pointer"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Supported formats: JPG, PNG, WEBP (max 5MB)
                    </p>
                    {selectedFile && (
                      <p className="text-sm text-green-600 mt-2">
                        ✓ Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="image_url">Image URL *</Label>
                    <Input
                      id="image_url"
                      name="image_url"
                      value={formData.image_url}
                      onChange={handleChange}
                      placeholder="https://example.com/image.jpg"
                      required={uploadMethod === 'url'}
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Must be a direct link to an image (ending in .jpg, .png, .webp, etc.)
                    </p>
                  </div>
                )}

                <div>
                  <Label htmlFor="caption">Caption (Optional)</Label>
                  <Textarea
                    id="caption"
                    name="caption"
                    value={formData.caption}
                    onChange={handleChange}
                    placeholder="Describe this image..."
                    rows={2}
                  />
                </div>

                <div>
                  <Label htmlFor="display_order">Display Order</Label>
                  <Input
                    id="display_order"
                    name="display_order"
                    type="number"
                    value={formData.display_order}
                    onChange={handleChange}
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Lower numbers appear first. Use 0 for newest images.
                  </p>
                </div>

                {formData.image_url && (
                  <div>
                    <Label>Image Preview</Label>
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="mt-2 max-w-md max-h-64 object-contain rounded border"
                      onError={(e) => {
                        if (uploadMethod === 'url') {
                          e.target.style.display = 'none';
                          toast({
                            title: 'Invalid URL',
                            description: 'Unable to load image from this URL',
                            variant: 'destructive'
                          });
                        }
                      }}
                    />
                  </div>
                )}

                <div className="flex gap-3">
                  <Button 
                    type="submit"
                    disabled={loading || uploading || (uploadMethod === 'file' && !selectedFile)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {uploading || loading ? 'Uploading...' : 'Add to Gallery'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddForm(false);
                      setSelectedFile(null);
                      setFormData({
                        image_url: '',
                        caption: '',
                        display_order: 0
                      });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* Gallery Grid */}
          <div className="mb-4">
            <h2 className="text-xl font-bold text-[#1e3a5f]">
              Gallery Images ({images.length})
            </h2>
          </div>

          {images.length === 0 ? (
            <Card className="p-12 text-center">
              <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No images in gallery yet</p>
              <Button
                onClick={() => setShowAddForm(true)}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Image
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {images.map((image) => (
                <Card key={image.id} className="overflow-hidden">
                  <div className="relative aspect-square">
                    <img
                      src={image.image_url}
                      alt={image.caption || 'Gallery image'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    {image.caption && (
                      <p className="text-sm text-gray-700 mb-3">{image.caption}</p>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Order: {image.display_order}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteImage(image.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Added: {new Date(image.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setImageToDelete(null);
        }}
        onConfirm={confirmDeleteImage}
        title="Delete Image"
        message="Are you sure you want to delete this image? This action cannot be undone."
        confirmText="Delete Image"
        variant="danger"
        loading={uploading}
      />
      
      <Footer />
    </div>
  );
};

export default AdminGallery;
